import React, { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { toast } from 'sonner';
import { ChevronRight, Upload, FileText } from 'lucide-react';

// ✅ Config Imports
import { IS_DEV, TEST_REAL_API } from "@/config/env";

// ✅ Validation Imports
import { medicalRegistrationSchema } from './doctorValidation';
import type { MedicalRegistrationData } from './doctorValidation';

// ✅ API Import
import { saveMedicalRegistration } from '../../../../../api/onboardingApis/doctorOnboardingApi/doctorMedicalRegistration.api';

interface DoctorStep4Props {
  onNext: (data: MedicalRegistrationData) => void;
}

export default function DoctorStep4({ onNext }: DoctorStep4Props) {
  const [fileName, setFileName] = useState<string>("");
  const [isDragging, setIsDragging] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const {
    register,
    handleSubmit,
    formState: { errors, isValid },
    setValue,
    clearErrors,
    trigger, // 🔥 Added trigger
  } = useForm<MedicalRegistrationData>({
    resolver: zodResolver(medicalRegistrationSchema),
    mode: "all",
    shouldUnregister: true, 
  });

  // 🧹 Effect: Register the file field manually
  useEffect(() => {
    register("medicalRegistration");
  }, [register]);

  // ⚡ DEV SKIP HANDLER
  const handleDevSkip = async () => {
 
    await new Promise(resolve => setTimeout(resolve, 800));

    // Mock Data
    const mockData: MedicalRegistrationData = {
      medicalRegistration: new DataTransfer().files 
    };

    toast.success("⚡ Mock Registration Proof Saved (Dev Mode)");
    onNext(mockData);
  };

  // 🟢 SUBMIT HANDLER
  const onSubmit = async (data: MedicalRegistrationData) => {
    setIsSubmitting(true);
    try {
      if (IS_DEV && !TEST_REAL_API) {
         await handleDevSkip();
         setIsSubmitting(false);
         return; 
      }

    

      const expertCode = localStorage.getItem('expertCode');
      if (!expertCode) {
        toast.error("Session missing. Please login again.");
        setIsSubmitting(false);
        return;
      }

      const formData = new FormData();
      formData.append('expertCode', expertCode);
      
      if (data.medicalRegistration && data.medicalRegistration.length > 0) {
        formData.append('file', data.medicalRegistration[0]); 
      }

      await saveMedicalRegistration(formData);

      toast.success("Medical Registration uploaded successfully!");
      onNext(data);

    } catch (error: any) {
      console.error("API Error:", error);
      toast.error(error.response?.data?.message || "Failed to upload document. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (files && files.length > 0) {
      setFileName(files[0].name);
      
      // 🔥 FIX: Set value and Force Validation
      setValue("medicalRegistration", files, { shouldValidate: true });
      trigger("medicalRegistration");
      clearErrors("medicalRegistration");
    }
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
    const files = e.dataTransfer.files;
    if (files && files.length > 0) {
      setFileName(files[0].name);
      
      // 🔥 FIX: Set value and Force Validation
      setValue("medicalRegistration", files, { shouldValidate: true });
      trigger("medicalRegistration");
      clearErrors("medicalRegistration");
    }
  };

  const isStrictMode = !IS_DEV || TEST_REAL_API;

  return (
    <div className="bg-white rounded-lg shadow-xl p-8">
      
      <div className="flex justify-between items-start mb-6">
        <div>
           <h2 className="text-2xl font-bold text-gray-800 mb-2">Medical Registration Proof</h2>
           <p className="text-gray-600">Please upload your medical registration proof</p>
        </div>
        
        {TEST_REAL_API && IS_DEV && (
          <div className="bg-yellow-500/20 text-yellow-700 py-1 px-3 rounded-full text-xs border border-yellow-500/50 font-medium">
            ⚠️ Real API Testing Enabled
          </div>
        )}
      </div>

      <div>
        <div
          className={`border-2 border-dashed rounded-lg p-8 text-center transition-all ${
            isDragging 
              ? 'border-cyan-500 bg-cyan-50' 
              : errors.medicalRegistration 
                ? 'border-red-500 bg-red-50' 
                : 'border-gray-300 hover:border-cyan-400 bg-gray-50'
          }`}
          onDragOver={handleDragOver}
          onDragLeave={() => setIsDragging(false)}
          onDrop={handleDrop}
        >
          {/* 🔥 FIX: Removed ...register() */}
          <input
            type="file"
            onChange={handleFileChange}
            accept=".pdf,.jpg,.jpeg,.png"
            className="hidden"
            id="medical-upload"
          />
          <label htmlFor="medical-upload" className="cursor-pointer w-full h-full block">
            <div className="flex flex-col items-center justify-center">
              {fileName ? (
                <>
                  <FileText className="w-16 h-16 text-cyan-500 mb-3" />
                  <p className="text-sm font-medium text-gray-700">{fileName}</p>
                  <p className="text-xs text-gray-500 mt-1">Click to replace file</p>
                </>
              ) : (
                <>
                   <Upload className="w-16 h-16 text-gray-400 mb-3" />
                   <div className="bg-white border border-gray-300 text-cyan-500 px-6 py-2 rounded-lg mb-2 shadow-sm">
                      Upload File
                   </div>
                   <p className="text-sm text-gray-500">Drag & drop or click to upload</p>
                </>
              )}
            </div>
          </label>
        </div>
        
        {errors.medicalRegistration && (
             <p className="mt-2 text-sm text-red-600">
                {errors.medicalRegistration.message as string}
             </p>
        )}
        
        <div className="mt-6 bg-blue-50 p-4 rounded-lg border border-blue-100 text-sm text-gray-600">
          <p className="mb-2">Only licensed and genuine doctors are listed on our platform.</p>
          <p className="font-semibold mb-1">Acceptable documents:</p>
          <ul className="list-disc list-inside space-y-1 ml-1">
             <li>Medical Registration Certificate</li>
          </ul>
        </div>
      </div>

      <div className="text-center mt-8 space-y-3">
        
        {/* ⚡ DEV BUTTON */}
        {IS_DEV && !TEST_REAL_API && (
            <button
              type="button"
              onClick={handleDevSkip}
              className="w-full bg-yellow-400 hover:bg-yellow-500 text-black font-semibold py-2 px-12 rounded-lg transition-colors border-2 border-yellow-600 border-dashed"
            >
              ⚡ DEV MODE: Skip & Mock Verify
            </button>
        )}

        <button
          type="button"
          onClick={handleSubmit(onSubmit)}
          disabled={isSubmitting || (isStrictMode && !isValid)}
          className={`w-full bg-cyan-400 hover:bg-cyan-500 text-white font-semibold py-3 px-12 rounded-lg transition-colors 
            ${(isSubmitting || (isStrictMode && !isValid)) ? 'opacity-50 cursor-not-allowed' : ''}
          `}
        >
          {isSubmitting ? "Processing..." : (
             <span className="flex items-center justify-center gap-2">
               Proceed to Next Step <ChevronRight size={20} />
             </span>
          )}
        </button>
      </div>
    </div>
  );
}